import type { ThresholdSport } from '@painpal/contracts';
import { weekStart } from '@painpal/core';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext, createRoute, createRouter, Link, Navigate, Outlet, redirect, useRouter, type ErrorComponentProps, type ParsedLocation, type RouterHistory,
} from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { SessionUnavailableError, sessionQueryOptions } from '../auth/session';
import { syncTimezone } from '../auth/timezone';
import { EmptyState, ErrorPanel } from '../components/StateViews';
import { ActivitiesPage } from '../features/activities/ActivitiesPage';
import { ActivityDetailPage } from '../features/activity/ActivityDetailPage';
import { CalendarPage } from '../features/calendar/CalendarPage';
import { SignInPage } from '../features/auth/SignInPage';
import { FitnessPage } from '../features/fitness/FitnessPage';
import { FtpStepPage } from '../features/onboarding/FtpStepPage';
import { RidesStepPage } from '../features/onboarding/RidesStepPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { todayLocal } from '../lib/dates';
import { AppLayout } from './AppLayout';
import { parseActivitiesSearch, parseCalendarSearch, parseFitnessSearch, parseSettingsSearch, parseSignInSearch, parseStravaReturnSearch } from './search';
import styles from './app.module.css';

export interface RouterContext { queryClient: QueryClient }

function NotFound(): ReactElement {
  return (
    <div className={styles.notFound}>
      <EmptyState title="PAGE NOT FOUND" body="That page doesn't exist.">
        <Link to="/" className="btn btn-ink">BACK TO FITNESS</Link>
      </EmptyState>
    </div>
  );
}

const ROUTE_ERROR_BODY = 'Something went wrong loading this page. Try again, or head back to Fitness.';

function RouteError({ error }: ErrorComponentProps): ReactElement {
  const router = useRouter();
  const unreachable = error instanceof SessionUnavailableError;
  return (
    <div className={styles.notFound}>
      <ErrorPanel
        error={error} onRetry={() => void router.invalidate()}
        title={unreachable ? 'COULDN’T REACH PAINPAL' : 'SOMETHING WENT WRONG'}
        body={unreachable ? error.message : ROUTE_ERROR_BODY}
      >
        <Link to="/" className="btn btn-ink">BACK TO FITNESS</Link>
      </ErrorPanel>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet, notFoundComponent: NotFound, errorComponent: RouteError });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'sign-in',
  validateSearch: parseSignInSearch,
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.fetchQuery(sessionQueryOptions()).catch(() => null);
    if (user !== null) throw redirect({ to: '/' });
  },
  component: SignInRoute,
});

function SignInRoute(): ReactElement {
  const search = signInRoute.useSearch();
  return <SignInPage mode={search.mode ?? 'sign-in'} error={search.error} />;
}

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.fetchQuery(sessionQueryOptions());
    if (user === null) throw redirect({ to: '/sign-in' });
    return { user: await syncTimezone(user, context.queryClient) };
  },
  component: Outlet,
});

const ftpRoute = createRoute({ getParentRoute: () => authedRoute, path: 'onboarding/ftp', component: FtpRoute });
function FtpRoute(): ReactElement {
  return <FtpStepPage today={todayLocal()} />;
}

const ridesRoute = createRoute({
  getParentRoute: () => authedRoute, path: 'onboarding/rides', validateSearch: parseStravaReturnSearch, component: RidesRoute,
});
function RidesRoute(): ReactElement {
  const { error } = ridesRoute.useSearch();
  return <RidesStepPage stravaError={error} />;
}

const shellRoute = createRoute({ getParentRoute: () => authedRoute, id: 'shell', component: ShellRoute });
function ShellRoute(): ReactElement {
  const { user } = shellRoute.useRouteContext();
  return <AppLayout user={user} />;
}

const fitnessRoute = createRoute({ getParentRoute: () => shellRoute, path: '/', validateSearch: parseFitnessSearch, component: FitnessRoute });
function FitnessRoute(): ReactElement {
  const { sport } = fitnessRoute.useSearch();
  return <FitnessPage today={todayLocal()} sport={sport ?? 'all'} />;
}

const calendarRoute = createRoute({
  getParentRoute: () => shellRoute, path: 'calendar', validateSearch: parseCalendarSearch, component: CalendarRoute,
});
function CalendarRoute(): ReactElement {
  const { week } = calendarRoute.useSearch();
  const today = todayLocal();
  return <CalendarPage today={today} week={week ?? weekStart(today)} />;
}

const activitiesRoute = createRoute({
  getParentRoute: () => shellRoute, path: 'activities', validateSearch: parseActivitiesSearch, component: ActivitiesRoute,
});
function ActivitiesRoute(): ReactElement {
  const { sport, upload } = activitiesRoute.useSearch();
  return <ActivitiesPage sport={sport ?? 'all'} uploadOpen={upload === true} />;
}

const activityRoute = createRoute({ getParentRoute: () => shellRoute, path: 'activities/$activityId', component: ActivityRoute });
function ActivityRoute(): ReactElement {
  const { activityId } = activityRoute.useParams();
  return <ActivityDetailPage activityId={activityId} />;
}

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute, path: 'settings', validateSearch: parseSettingsSearch, component: SettingsRoute,
});
function SettingsRoute(): ReactElement {
  // Route context is a snapshot from beforeLoad; the live query keeps the Account card current after the email changes.
  // A 401 clears the cache and sign in refetches the session before this page unmounts, so the query can be empty: keep the snapshot then.
  const contextUser = settingsRoute.useRouteContext().user;
  const { data: user = contextUser } = useQuery(sessionQueryOptions());
  const { error, sport } = settingsRoute.useSearch();
  const navigate = settingsRoute.useNavigate();
  // A refetch returns null once the session has expired; leave the same way authedRoute does.
  if (user === null) return <Navigate to="/sign-in" />;
  // A push, so back returns to the sport you were on. RIDE is the default and stays out of the URL; the Strava return error is dropped.
  const onSportChange = (next: ThresholdSport): void => void navigate({ search: { sport: next === 'ride' ? undefined : next } });
  return <SettingsPage user={user} today={todayLocal()} stravaError={error} sport={sport ?? 'ride'} onSportChange={onSportChange} />;
}

export const routeTree = rootRoute.addChildren([
  signInRoute,
  authedRoute.addChildren([
    ftpRoute,
    ridesRoute,
    shellRoute.addChildren([fitnessRoute, calendarRoute, activitiesRoute, activityRoute, settingsRoute]),
  ]),
]);

// Search-only changes (sport filter, ?upload=true) keep the page and must not cross-fade it. The initial
// load has no previous location and nothing to fade from.
export function pageTransitionTypes({ fromLocation, pathChanged }: { fromLocation?: ParsedLocation; pathChanged: boolean }): string[] | false {
  return fromLocation !== undefined && pathChanged ? ['page'] : false;
}

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree, context: { queryClient }, history, defaultPreload: 'intent', defaultErrorComponent: RouteError,
    defaultViewTransition: { types: pageTransitionTypes },
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
